import imgTextPreview from "assets/img/text-preview.svg"
import IconDropdownIndicator from "assets/img/icon-dropdown-indicator.svg?react"

import { FC, useState, useRef, useLayoutEffect, ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { Flex } from "components/Flex"
import Text, { TextType } from "components/Text/Text"
import Button from "components/Button/Button"
import TextBuildingBlocksModal from "components/TextBuildingBlocksModal/TextBuildingBlocksModal"
import { TextBuildingBlockMode } from "components/TextBuildingBlocksModal/TextBuildingBlock"
import { Grid, GridItem } from "components/Grid"
import TextModal from "components/TextModal/TextModal"
import { InfoDropdown, InfoDropdownList, InfoDropdownListItemButton } from "components/InfoDropdown"
import { TextVariable, TextVariableGroup } from "config/constants"
import InsertTextVariableDropdown from "components/InsertTextVariableDropdown/InsertTextVariableDropdown"

interface ITextSectionProps {
    title?: string
    titleType?: TextType
    bodyClassName?: string
    popupTitle?: string
    text: string
    formattingRuleLabel?: string
    onChange?: (value: string) => void
    lineBreakAfter?: number
    multiInsertOptions?: boolean
    orderConfirmationText?: string
    monospace?: boolean
    textFormatted?: boolean
    textVariables?: boolean
    textVariableGroup?: TextVariableGroup
    onInsertConfiguration?(insertText: (text: string) => void): void
    renderContent?(params: {
        openViewModal?: () => void
        openEditModal?: () => void
        textBlockRef?: React.RefObject<HTMLDivElement | null>
    }): ReactNode
}

const TextSection: FC<ITextSectionProps> = ({
    title,
    titleType,
    bodyClassName: bodyClassNameProp,
    popupTitle,
    text,
    formattingRuleLabel,
    onChange,
    lineBreakAfter,
    multiInsertOptions,
    orderConfirmationText,
    monospace,
    textFormatted,
    textVariables,
    textVariableGroup,
    onInsertConfiguration,
    renderContent,
}) => {
    const { t } = useTranslation()
    const [isReading, setIsReading] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [editingText, setEditingText] = useState('')
    const [showTextBuildingBlocksModal, setShowTextBuildingBlocksModal] = useState(false)
    const textBlockRef = useRef<HTMLDivElement>(null)
    const [isShowMoreEnabled, setShowMoreEnabled] = useState(false)

    //Detect that text is clamped
    useLayoutEffect(() => {
        if (!textBlockRef.current) return
        const elem = textBlockRef.current
        setShowMoreEnabled(elem.scrollHeight > elem.clientHeight)
    }, [text, textBlockRef])

    function setupEditing() {
        if (!onChange) return
        setEditingText(text);
        setIsEditing(true);
    }

    function handleSelectionInsert(selectedText: string) {
        const textToAdd = textFormatted
            ? selectedText
                .split('\n')
                .map(line => `<p>${line.replace(/ {2}/g, ' &nbsp;')}</p>`)
                .join('')
            : selectedText

        setEditingText(editingText + (editingText && !textFormatted ? "\n" : "") + textToAdd)
    }

    async function handleSaveChanges(newText: string) {
        if (!onChange) return
        setEditingText(newText)
        onChange(newText)
        setIsEditing(false)
    }

    function handleInsertOrderAcknowledgement() {
        setEditingText(orderConfirmationText || '');
    }

    const formattingRuleBlock = formattingRuleLabel ? (
        <>
            <div className="mb-2">{t("textModal.formattingRules")}</div>
            <div>{formattingRuleLabel}</div>
        </>
    ) : null

    const readModalFooter = onChange ? (
        <Flex justifyContent="flex-end" className="mt-4">
            <Button theme="link"
                label={t('textModal.changeText')}
                onClick={setupEditing} />
        </Flex>
    ) : null

    function getEditModalDescription(params?: { onInsertTextVariableClick?: (textVariable: TextVariable) => void }) {
        return (
            <Grid
                columns="minmax(0, auto) max-content"
                gap="3.2rem"
                alignItems="end"
                justifyContent="space-between">
                <GridItem>
                    {formattingRuleBlock}
                </GridItem>
                <Flex alignItems="center" gap="2.4rem">
                    {!!params?.onInsertTextVariableClick &&
                        <InsertTextVariableDropdown
                            onTextVariableSelect={params.onInsertTextVariableClick}
                            textVariableGroup={textVariableGroup}
                        />
                    }
                    {!multiInsertOptions
                        ? <Button
                            theme="link"
                            label={t("reservation.insertTextBuildingBlocks")}
                            onClick={() => setShowTextBuildingBlocksModal(true)}
                        />
                        : <InfoDropdown
                            label={() => (
                                <Button
                                    theme="link"
                                    label={t("reservation.insertText")}
                                    icon={<IconDropdownIndicator />}
                                    iconPosition="end"
                                />
                            )}
                            placement="bottom-start"
                            content={close =>
                                <InfoDropdownList>
                                    <InfoDropdownListItemButton
                                        onClick={() => { close(); setShowTextBuildingBlocksModal(true) }}>
                                        {t("reservation.textBuildingBlocks")}
                                    </InfoDropdownListItemButton>
                                    <InfoDropdownListItemButton
                                        onClick={() => { close(); handleInsertOrderAcknowledgement() }}>
                                        {t("reservation.orderAcknowledgement")}
                                    </InfoDropdownListItemButton>
                                </InfoDropdownList>
                            }
                        />
                    }
                    {!!onInsertConfiguration && !!onChange &&
                        <Button
                            theme="link"
                            label={t("textModal.insertConfigurationText")}
                            onClick={() => onInsertConfiguration((insertedText: string) => {
                                if (textFormatted) {
                                    const htmlText = insertedText
                                        .split('\n')
                                        .map(line => `<p>${line.replace(/\r/g, '').replace(/ {2}/g, ' &nbsp;')}</p>`)
                                        .join('')
                                    setEditingText(htmlText)
                                } else {
                                    setEditingText(insertedText)
                                }
                            })}
                        />
                    }
                </Flex>
            </Grid>
        )
    }

    const bodyClassName = bodyClassNameProp ?? `bg-grey-50 round-sm ${text ? 'h-100' : 'p-2'}`

    return (
        <>
            {renderContent
                ? renderContent({
                    openViewModal: isShowMoreEnabled ? () => setIsReading(true) : undefined,
                    openEditModal: onChange ? setupEditing : undefined,
                    textBlockRef,
                })
                : <Flex flow="column">
                    {(!!title || !!(text && onChange)) &&
                        <Flex alignItems="center" justifyContent="space-between" className="my-1-5">
                            {!!title && <Text type={titleType}>{title}</Text>}
                            {!!text && !!onChange && <Button theme="link"
                                label={t('textModal.changeText')}
                                onClick={setupEditing} />
                            }
                        </Flex>
                    }
                    {text
                        ? <Flex className={bodyClassName}
                            flow="column"
                            alignItems="center"
                            justifyContent="flex-end">
                            <img src={imgTextPreview} alt="" className="d-block max-w-100" />
                        </Flex>
                        : <Flex className={bodyClassName} flow="column" alignItems="center">
                            <Text className="mt-4 mb-3 px-4 text-center">{onChange ? t('reservation.textSection.noTextDefinedWantToAdd') : t('reservation.textSection.noTextDefined')}</Text>
                            {!!onChange &&
                                <Button
                                    theme="secondary"
                                    className="mb-4"
                                    label={t('reservation.textSection.addText')}
                                    onClick={setupEditing}
                                />
                            }
                        </Flex>
                    }
                </Flex>
            }
            {isReading &&
                <TextModal
                    title={popupTitle}
                    text={text}
                    onClose={() => setIsReading(false)}
                    getDescription={() => formattingRuleBlock}
                    overrideFooter={readModalFooter}
                    lineBreakAfter={lineBreakAfter}
                    monospace={monospace}
                    textFormatted={textFormatted}
                />
            }
            {isEditing &&
                <TextModal
                    title={popupTitle}
                    text={editingText}
                    onChangeText={setEditingText}
                    onClose={() => setIsEditing(false)}
                    getDescription={getEditModalDescription}
                    onSave={handleSaveChanges}
                    lineBreakAfter={lineBreakAfter}
                    monospace={monospace}
                    textFormatted={textFormatted}
                    textVariables={textVariables}
                />
            }
            {showTextBuildingBlocksModal &&
                <TextBuildingBlocksModal
                    title={popupTitle}
                    mode={TextBuildingBlockMode.select}
                    onSelectionInsert={handleSelectionInsert}
                    onClose={() => setShowTextBuildingBlocksModal(false)}
                    notFormattedOnly={!textFormatted}
                />
            }
        </>
    )
}

export default TextSection
